import { useState, useEffect, useCallback, useRef } from 'react';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './ui/tooltip';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    Users,
    Clock,
    UserCheck,
    Utensils,
    Sparkles,
    Ban,
    AlertTriangle,
    MoreVertical,
    Eye,
    Edit2,
    UserPlus,
    ClipboardList,
    XCircle,
    Check,
    Trash2
} from 'lucide-react';

// Couleurs des statuts de table
const TABLE_STATUS_COLORS = {
    disponible: {
        bg: 'bg-emerald-100 hover:bg-emerald-200',
        border: 'border-emerald-400',
        text: 'text-emerald-700',
        fill: '#10b981'
    },
    reservee: {
        bg: 'bg-blue-100 hover:bg-blue-200',
        border: 'border-blue-400',
        text: 'text-blue-700',
        fill: '#3b82f6'
    },
    occupee: {
        bg: 'bg-orange-100 hover:bg-orange-200',
        border: 'border-orange-400',
        text: 'text-orange-700',
        fill: '#f97316'
    },
    a_debarrasser: {
        bg: 'bg-amber-100 hover:bg-amber-200',
        border: 'border-amber-400',
        text: 'text-amber-700',
        fill: '#f59e0b'
    },
    en_nettoyage: {
        bg: 'bg-purple-100 hover:bg-purple-200',
        border: 'border-purple-400',
        text: 'text-purple-700',
        fill: '#a855f7'
    },
    hors_service: {
        bg: 'bg-gray-200 hover:bg-gray-300',
        border: 'border-gray-400',
        text: 'text-gray-500',
        fill: '#9ca3af'
    }
};

const TABLE_STATUS_LABELS = {
    disponible: 'Disponible',
    reservee: 'Réservée',
    occupee: 'Occupée',
    a_debarrasser: 'À débarrasser',
    en_nettoyage: 'En nettoyage',
    hors_service: 'Hors service'
};

const TABLE_STATUS_ICONS = {
    disponible: Check,
    reservee: Clock,
    occupee: Utensils,
    a_debarrasser: Trash2,
    en_nettoyage: Sparkles,
    hors_service: Ban
};

// Composant Table individuelle
const TableShape = ({ 
    table, 
    isSelected, 
    onSelect, 
    onAction,
    scale = 1 
}) => {
    const status = table.effective_status || table.status || 'disponible';
    const colors = TABLE_STATUS_COLORS[status] || TABLE_STATUS_COLORS.disponible;
    const StatusIcon = TABLE_STATUS_ICONS[status] || Check;
    
    const width = (table.width || 60) * scale;
    const height = (table.height || 60) * scale;
    const x = (table.position_x || 0) * scale;
    const y = (table.position_y || 0) * scale;
    
    const hasReservation = table.current_reservation || table.next_reservation;
    const reservation = table.current_reservation || table.next_reservation;
    
    return (
        <TooltipProvider>
            <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <g 
                                className="cursor-pointer transition-all duration-200"
                                style={{ 
                                    transform: `translate(${x}px, ${y}px) rotate(${table.rotation || 0}deg)`,
                                    transformOrigin: `${width/2}px ${height/2}px`
                                }}
                                onClick={() => onSelect?.(table)}
                            >
                                {/* Forme de la table */}
                                {table.shape === 'round' ? (
                                    <circle
                                        cx={width / 2}
                                        cy={height / 2}
                                        r={Math.min(width, height) / 2 - 2}
                                        fill={colors.fill}
                                        fillOpacity={0.2}
                                        stroke={colors.fill}
                                        strokeWidth={isSelected ? 3 : 2}
                                        className="transition-all"
                                    />
                                ) : table.shape === 'rectangle' ? (
                                    <rect
                                        x={2}
                                        y={2}
                                        width={width - 4}
                                        height={height - 4}
                                        rx={6}
                                        fill={colors.fill}
                                        fillOpacity={0.2}
                                        stroke={colors.fill}
                                        strokeWidth={isSelected ? 3 : 2}
                                        className="transition-all"
                                    />
                                ) : (
                                    <rect
                                        x={2}
                                        y={2}
                                        width={width - 4}
                                        height={height - 4}
                                        rx={4}
                                        fill={colors.fill}
                                        fillOpacity={0.2}
                                        stroke={colors.fill}
                                        strokeWidth={isSelected ? 3 : 2}
                                        className="transition-all"
                                    />
                                )}
                                
                                {/* Numéro de table */}
                                <text
                                    x={width / 2}
                                    y={height / 2 - 5}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill={colors.fill}
                                    fontSize={14 * scale}
                                    fontWeight="bold"
                                >
                                    {table.number}
                                </text>
                                
                                {/* Capacité */}
                                <text
                                    x={width / 2}
                                    y={height / 2 + 10}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill={colors.fill}
                                    fontSize={10 * scale}
                                    opacity={0.8}
                                >
                                    {table.capacity} pers
                                </text>
                                
                                {/* Indicateur de réservation */}
                                {hasReservation && (
                                    <circle
                                        cx={width - 8}
                                        cy={8}
                                        r={6}
                                        fill="#3b82f6"
                                        stroke="white"
                                        strokeWidth={2}
                                    />
                                )}
                                
                                {/* Indicateur hors service */}
                                {status === 'hors_service' && (
                                    <line
                                        x1={5}
                                        y1={5}
                                        x2={width - 5}
                                        y2={height - 5}
                                        stroke="#ef4444"
                                        strokeWidth={2}
                                    />
                                )}
                            </g>
                        </DropdownMenuTrigger>
                        
                        <DropdownMenuContent align="start" className="w-48">
                            <div className="px-2 py-1.5">
                                <div className="font-semibold">Table {table.number}</div>
                                <div className="text-xs text-muted-foreground">
                                    {TABLE_STATUS_LABELS[status]} • {table.capacity} places
                                </div>
                            </div>
                            <DropdownMenuSeparator />
                            
                            {status === 'disponible' && (
                                <DropdownMenuItem onClick={() => onAction?.('reserve', table)}>
                                    <ClipboardList className="w-4 h-4 mr-2" />
                                    Nouvelle réservation
                                </DropdownMenuItem>
                            )}
                            
                            {status === 'reservee' && reservation && (
                                <>
                                    <DropdownMenuItem onClick={() => onAction?.('arrive', table)}>
                                        <UserCheck className="w-4 h-4 mr-2" />
                                        Client arrivé
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => onAction?.('seat', table)}>
                                        <UserPlus className="w-4 h-4 mr-2" />
                                        Installer
                                    </DropdownMenuItem>
                                </>
                            )}
                            
                            {status === 'occupee' && (
                                <DropdownMenuItem onClick={() => onAction?.('view-order', table)}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    Voir la commande
                                </DropdownMenuItem>
                            )}
                            
                            {status === 'a_debarrasser' && (
                                <DropdownMenuItem onClick={() => onAction?.('clear', table)}>
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Marquer débarrassée
                                </DropdownMenuItem>
                            )}
                            
                            {status === 'en_nettoyage' && (
                                <DropdownMenuItem onClick={() => onAction?.('ready', table)}>
                                    <Check className="w-4 h-4 mr-2" />
                                    Table prête
                                </DropdownMenuItem>
                            )}
                            
                            <DropdownMenuSeparator />
                            
                            <DropdownMenuItem onClick={() => onAction?.('edit', table)}>
                                <Edit2 className="w-4 h-4 mr-2" />
                                Modifier la table
                            </DropdownMenuItem>
                            
                            {status !== 'hors_service' ? (
                                <DropdownMenuItem 
                                    onClick={() => onAction?.('out-of-service', table)}
                                    className="text-red-600"
                                >
                                    <Ban className="w-4 h-4 mr-2" />
                                    Hors service
                                </DropdownMenuItem>
                            ) : (
                                <DropdownMenuItem onClick={() => onAction?.('ready', table)}>
                                    <Check className="w-4 h-4 mr-2" />
                                    Remettre en service
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </TooltipTrigger>
                
                <TooltipContent side="right" className="max-w-xs">
                    <div className="space-y-1">
                        <div className="font-semibold flex items-center gap-2">
                            <StatusIcon className="w-4 h-4" />
                            Table {table.number} - {TABLE_STATUS_LABELS[status]}
                        </div>
                        <div className="text-xs">
                            Zone: <span className="capitalize">{table.zone}</span> • {table.capacity} places
                        </div>
                        {reservation && (
                            <div className="pt-1 border-t mt-1">
                                <div className="font-medium">{reservation.customer_name}</div>
                                <div className="text-xs text-muted-foreground">
                                    {reservation.time} • {reservation.party_size} pers
                                </div>
                            </div>
                        )}
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

// Composant Légende
const FloorPlanLegend = () => (
    <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(TABLE_STATUS_COLORS).map(([status, colors]) => (
            <div key={status} className="flex items-center gap-1.5">
                <div 
                    className="w-3 h-3 rounded-full border-2"
                    style={{ backgroundColor: colors.fill + '33', borderColor: colors.fill }}
                />
                <span className="text-muted-foreground">{TABLE_STATUS_LABELS[status]}</span>
            </div>
        ))}
    </div>
);

// Composant Zone Label
const ZoneLabel = ({ zone, x, y, width }) => {
    const zoneNames = {
        terrasse: 'Terrasse',
        salle: 'Salle Principale',
        vip: 'Espace VIP',
        bar: 'Bar'
    };
    
    const zoneColors = {
        terrasse: '#f59e0b',
        salle: '#3b82f6',
        vip: '#8b5cf6',
        bar: '#ec4899'
    };
    
    return (
        <g>
            {/* Zone background */}
            <rect
                x={x - 10}
                y={y - 30}
                width={width + 20}
                height={25}
                rx={4}
                fill={zoneColors[zone]}
                fillOpacity={0.1}
            />
            <text
                x={x + width / 2 - 10}
                y={y - 15}
                textAnchor="middle"
                fill={zoneColors[zone]}
                fontSize={12}
                fontWeight="600"
            >
                {zoneNames[zone] || zone}
            </text>
        </g>
    );
};

// Composant Principal FloorPlan
export default function FloorPlan({
    tables = [],
    selectedTable,
    onSelectTable,
    onTableAction,
    className,
    showLegend = true,
    showZones = true,
    interactive = true
}) {
    const svgRef = useRef(null);
    const [viewBox, setViewBox] = useState({ x: 0, y: 0, width: 1000, height: 550 });
    const [scale, setScale] = useState(1);
    
    // Calculer les limites du plan
    useEffect(() => {
        if (tables.length > 0) {
            let maxX = 0, maxY = 0;
            tables.forEach(t => {
                const right = (t.position_x || 0) + (t.width || 60);
                const bottom = (t.position_y || 0) + (t.height || 60);
                if (right > maxX) maxX = right;
                if (bottom > maxY) maxY = bottom;
            });
            
            // Ajouter une marge
            setViewBox({
                x: -20,
                y: -50,
                width: Math.max(maxX + 100, 900),
                height: Math.max(maxY + 100, 500)
            });
        }
    }, [tables]);
    
    // Grouper les tables par zone
    const tablesByZone = tables.reduce((acc, table) => {
        const zone = table.zone || 'salle';
        if (!acc[zone]) acc[zone] = [];
        acc[zone].push(table);
        return acc;
    }, {});
    
    // Définir les positions des zones
    const zonePositions = {
        terrasse: { x: 30, y: 50, width: 220 },
        salle: { x: 300, y: 50, width: 280 },
        vip: { x: 650, y: 100, width: 200 },
        bar: { x: 650, y: 400, width: 250 }
    };
    
    return (
        <div className={cn("flex flex-col gap-4", className)}>
            {showLegend && <FloorPlanLegend />}
            
            <div className="relative bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-xl border overflow-hidden">
                {/* SVG Floor Plan */}
                <svg
                    ref={svgRef}
                    viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
                    className="w-full h-[500px]"
                    style={{ minHeight: '400px' }}
                >
                    {/* Grille de fond */}
                    <defs>
                        <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                            <path 
                                d="M 50 0 L 0 0 0 50" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeOpacity="0.05"
                            />
                        </pattern>
                    </defs>
                    <rect 
                        x={viewBox.x} 
                        y={viewBox.y} 
                        width={viewBox.width} 
                        height={viewBox.height} 
                        fill="url(#grid)" 
                    />
                    
                    {/* Labels des zones */}
                    {showZones && Object.entries(zonePositions).map(([zone, pos]) => (
                        tablesByZone[zone] && tablesByZone[zone].length > 0 && (
                            <ZoneLabel 
                                key={zone} 
                                zone={zone} 
                                x={pos.x} 
                                y={pos.y} 
                                width={pos.width}
                            />
                        )
                    ))}
                    
                    {/* Séparateurs de zone (lignes décoratives) */}
                    <line x1={270} y1={30} x2={270} y2={480} stroke="#e2e8f0" strokeWidth={2} strokeDasharray="5,5" />
                    <line x1={620} y1={30} x2={620} y2={480} stroke="#e2e8f0" strokeWidth={2} strokeDasharray="5,5" />
                    <line x1={620} y1={380} x2={900} y2={380} stroke="#e2e8f0" strokeWidth={2} strokeDasharray="5,5" />
                    
                    {/* Icônes décoratives des zones */}
                    <text x={130} y={420} fontSize={60} opacity={0.1}>☀️</text>
                    <text x={400} y={420} fontSize={60} opacity={0.1}>🍽️</text>
                    <text x={750} y={350} fontSize={40} opacity={0.1}>👑</text>
                    <text x={800} y={480} fontSize={40} opacity={0.1}>🍸</text>
                    
                    {/* Tables */}
                    {tables.map(table => (
                        <TableShape
                            key={table.id}
                            table={table}
                            isSelected={selectedTable?.id === table.id}
                            onSelect={interactive ? onSelectTable : undefined}
                            onAction={interactive ? onTableAction : undefined}
                            scale={scale}
                        />
                    ))}
                </svg>
                
                {/* Contrôles de zoom */}
                <div className="absolute bottom-4 right-4 flex gap-1 bg-white/90 dark:bg-slate-800/90 rounded-lg shadow p-1">
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
                    >
                        −
                    </Button>
                    <span className="flex items-center px-2 text-xs font-mono">
                        {Math.round(scale * 100)}%
                    </span>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={() => setScale(s => Math.min(2, s + 0.1))}
                    >
                        +
                    </Button>
                </div>
            </div>
        </div>
    );
}

// Export des constantes pour utilisation externe
export { TABLE_STATUS_COLORS, TABLE_STATUS_LABELS, TABLE_STATUS_ICONS, FloorPlanLegend };
